import { useState } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useSuppliers } from './hooks/useSuppliers';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus, Edit2, Trash2, Building2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SupplierModal } from './components/SupplierModal';
import type { Supplier } from './types/suppliers.types';

export function SuppliersPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.suppliers.view');

  const { data, isLoading, deleteSupplier, isDeleting } = useSuppliers();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Supplier | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);

  const handleEdit = (item: Supplier) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteSupplier(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <PageLayout title="Nhà cung cấp" description="Quản lý danh sách nhà cung cấp vật tư, dịch vụ">
      <div className="flex flex-col h-full gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Danh sách nhà cung cấp</h3>
          <AuthButton
            module="inventory.suppliers"
            action="create"
            behavior="disable"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 rounded-md inline-flex items-center gap-2 sm:text-sm text-xs"
            onClick={handleAdd}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Thêm nhà cung cấp</span>
          </AuthButton>
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-md overflow-hidden min-h-0 flex flex-col">
          <div className="flex-1 overflow-auto min-h-0 relative">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Tên & Mã</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Người liên hệ</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Đánh giá & Công nợ</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Trạng thái</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500 animate-pulse">Đang tải dữ liệu...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-12 text-center text-slate-500">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      Chưa có nhà cung cấp nào được thiết lập.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{item.code || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="sm:text-sm text-xs text-slate-700">{item.contact_person || '-'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{item.phone || item.email || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="sm:text-sm text-xs text-slate-700">
                          {item.rating ? `${item.rating} / 5 ⭐` : 'Chưa đánh giá'}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Công nợ: {item.debt_days ? `${item.debt_days} ngày` : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          item.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'
                        }`}>
                          {item.status === 'active' ? 'Đang hợp tác' : 'Ngừng giao dịch'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <AuthButton
                          module="inventory.suppliers"
                          action="update"
                          behavior="hide"
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </AuthButton>
                        <AuthButton
                          module="inventory.suppliers"
                          action="delete"
                          behavior="hide"
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </AuthButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <SupplierModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        supplier={editingItem}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa nhà cung cấp"
        message={
          <span>
            Bạn có chắc chắn muốn xóa nhà cung cấp <strong>{deleteTarget?.name}</strong>?
          </span>
        }
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}

export default SuppliersPage;
